import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import { OverlayContextProvider } from '@wix/da-react-context/pkg/OverlayContext';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import {
  BiData,
  BiContextProvidedValue,
  ClickCopyBiEvent,
  DownloadAllClickBiEvent,
} from '@wix/da-bi/pkg/events';
import Flex from '@wix/da-ds/pkg/Flex';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import DownloadIcon from '@wix/da-ds/pkg/Icons/24x24/Download';
import ShareIcon from '@wix/da-ds/pkg/Icons/24x24/Share';
import Loader from '@wix/da-ds/pkg/Loader';
import UserAvatar from '@wix/da-shared-react/pkg/User/Avatar/UserAvatar';
import FolderHeaderDesktop from '../../components/stash/FolderHeaderDesktop';
import DeviationTorpedo from '@wix/da-shared-react/pkg/Torpedo/DeviationTorpedo';
import OnScrollBottom, { DISTANCE_UNIT } from '@wix/da-shared-react/pkg/OnScrollBottom';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { Props } from './StashFolder';
import { STASH_FOLDER_DEVIATIONS_STREAM } from '../../../constants';
import StudioThumb from '../../components/StudioThumb';

import s from './StashFolderDesktop.scss';

const torpedoContainer = { width: 450, maxRows: 3 };

const StashFolderDesktop: FC<Props> = ({
  folder,
  isFetchingFolders,
  fetchMoreResults,
  copyFolderLink,
}) => {
  const { t } = useTranslation();
  const { streamId, streamConfig, streamThrottle } = STASH_FOLDER_DEVIATIONS_STREAM;
  const {
    items: deviations,
    isFetching,
    hasMore,
  } = useNormalizedDeviationStream(streamId, streamThrottle, streamConfig);

  const onShareClick = useCallback(() => copyFolderLink(folder), [copyFolderLink, folder]);

  const hasDeviations = deviations.length > 0;

  return (
    <Flex className={s['root']} direction="column" gap={24}>
      <Flex className={s['header']} alignItems="center" gap={10}>
        <Flex className={s['stash-owner']} gap={10}>
          <UserAvatar user={folder.owner} size={24} />
          <div className={s['stash-title']}>
            {t('stash.folder.title', { username: folder.owner.username })}
          </div>
        </Flex>
        <Flex alignItems="center" gap={24}>
          <TextButton
            icon={DownloadIcon}
            size="medium"
            variant="main"
            href={Url.stashFolderZipDownload(folder.folderId)}
            biData={BiData<DownloadAllClickBiEvent>({
              evid: 18,
              typeid: BiContextProvidedValue,
              itemid: BiContextProvidedValue,
              view: BiContextProvidedValue,
              component: BiContextProvidedValue,
              sectionname: 'folder_header',
            })}
          >
            {t('stash.folder.actions.download')}
          </TextButton>
          <TextButton
            icon={ShareIcon}
            size="medium"
            variant="main"
            onClick={onShareClick}
            biData={BiData<ClickCopyBiEvent>({
              evid: 357,
              typeid: BiContextProvidedValue,
              itemid: BiContextProvidedValue,
              view: BiContextProvidedValue,
              component: BiContextProvidedValue,
              sectionname: 'folder_header',
            })}
          >
            {t('stash.folder.actions.share')}
          </TextButton>
        </Flex>
      </Flex>
      <Flex direction="column">
        <div className={s['folder-name']}>{folder.name}</div>
        <FolderHeaderDesktop />

        {hasDeviations && (
          <BiLoggerContextProvider value={{ sectionname: 'files' }}>
            <OverlayContextProvider
              value={{
                showFaveControl: false,
                showMlt: false,
                showAwardBadge: false,
              }}
            >
              <DeviationTorpedo
                items={deviations}
                itemRenderer={(renderProps, item, itemStyle) => (
                  <StudioThumb
                    deviation={item.dto.deviation}
                    width={item.width}
                    height={item.height}
                    style={itemStyle}
                  />
                )}
                container={torpedoContainer}
                cookieType={MeasuredCookieType.STASH_FOLDER_TORPEDO}
                enableScrollVisibilityOptimisation={false}
              />
            </OverlayContextProvider>
          </BiLoggerContextProvider>
        )}

        {!isFetching && hasMore && (
          <OnScrollBottom
            checkOnMount
            checkOnUpdate={hasMore}
            onScrolledToBottom={() => fetchMoreResults()}
            distanceToBottom={100}
            distanceUnit={DISTANCE_UNIT.PX}
          />
        )}

        {(isFetching || isFetchingFolders) && (
          <div className={s['loading']}>
            <Loader className={s['sticky']} state="loading" size="big" />
          </div>
        )}
      </Flex>
    </Flex>
  );
};
StashFolderDesktop.displayName = 'StashFolderDesktop';

export default StashFolderDesktop;
